import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { MessageService } from 'primeng/api';
import { RequisitiService } from '../../../services/requisiti.service';
import { CommessaModel } from '../../../core/models/api/commessa.model';
import { ClienteModel } from '../../../core/models/api/cliente.model';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-create-commessa-dialog',
  standalone: false,
  templateUrl: './create-commessa-dialog.component.html',
  styleUrl: './create-commessa-dialog.component.scss'
})
export class CreateCommessaDialogComponent implements OnChanges {
  @Input() visible: boolean = false;
  @Input() commesse: CommessaModel[] = [];
  @Input() preselectedClienteId: number | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() commessaCreated = new EventEmitter<void>();

  codice: string = '';
  descrizione: string = '';
  clienteId: number | null = null;
  saving: boolean = false;
  loadingClienti: boolean = false;

  clienti: ClienteModel[] = [];
  clientiOptions: { label: string; value: number }[] = [];

  constructor(
    private requisitiService: RequisitiService,
    private messageService: MessageService,
    private logger: NGXLogger
  ) {}

  // Nessun ngOnInit: i clienti si caricano all'apertura del dialog (ngOnChanges).
  // Caricarli all'avvio significava una chiamata inutile a ogni caricamento della
  // home e un errore mostrato all'utente per un dialog che non ha aperto.
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && changes['visible'].currentValue === true) {
      // Reset del form quando il dialog viene aperto
      this.resetForm();
      // Ricarica i clienti ogni volta che il dialog viene aperto
      this.loadClienti();
      // Se c'è un cliente preselezionato, impostalo
      if (this.preselectedClienteId) {
        this.clienteId = this.preselectedClienteId;
      }
    }
  }

  /**
   * Carica i clienti disponibili
   */
  loadClienti(): void {
    this.loadingClienti = true;
    this.requisitiService.getClienti().subscribe({
      next: (clienti) => {
        this.clienti = clienti;
        this.clientiOptions = clienti.map(c => ({
          label: c.ragioneSociale,
          value: c.id
        }));
        this.loadingClienti = false;
        this.logger.debug('[CREATE-COMMESSA-DIALOG] Clienti caricati:', clienti.length);
      },
      error: (error) => {
        this.logger.error('[CREATE-COMMESSA-DIALOG] Errore nel caricamento dei clienti:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Dati non disponibili',
          detail: 'Impossibile contattare il server. Controlla la connessione e riprova.',
          // Più lungo dei 5s degli altri errori: compare all'avvio della home,
          // quando l'utente non sta ancora guardando un'azione che ha appena fatto.
          life: 15000
        });
        this.loadingClienti = false;
      }
    });
  }

  /**
   * Verifica se esiste già una commessa con lo stesso codice (case-insensitive)
   */
  codiceEsiste(): boolean {
    if (!this.codice.trim()) {
      return false;
    }

    const codiceNormalized = this.codice.trim().toLowerCase();
    return this.commesse.some(
      commessa => commessa.codice.toLowerCase() === codiceNormalized
    );
  }

  /**
   * Verifica se il form è valido
   */
  isFormValid(): boolean {
    return !!(
      this.codice.trim() &&
      this.descrizione.trim() &&
      this.clienteId &&
      !this.codiceEsiste()
    );
  }

  /**
   * Salva la nuova commessa
   */
  async saveCommessa(): Promise<void> {
    if (!this.isFormValid()) {
      if (this.codiceEsiste()) {
        this.messageService.add({
          severity: 'error',
          summary: 'Errore Validazione',
          detail: 'Esiste già una commessa con questo codice',
          life: 5000
        });
      } else {
        this.messageService.add({
          severity: 'error',
          summary: 'Errore Validazione',
          detail: 'Compila tutti i campi obbligatori',
          life: 5000
        });
      }
      return;
    }

    this.saving = true;

    try {
      const nuovaCommessa: Omit<CommessaModel, 'id' | 'clienteRagioneSociale'> = {
        codice: this.codice.trim(),
        descrizione: this.descrizione.trim(),
        clienteId: this.clienteId!,
        chiusa: false, // Nuove commesse sono sempre aperte
        numeroReferenze: 0 // Inizialmente nessuna referenza
      };

      await firstValueFrom(this.requisitiService.createCommessa(nuovaCommessa));

      this.logger.info('[CREATE-COMMESSA-DIALOG] Commessa creata con successo');

      this.messageService.add({
        severity: 'success',
        summary: 'Successo',
        detail: 'Commessa creata con successo',
        life: 3000
      });

      this.commessaCreated.emit();
      this.closeDialog();
    } catch (error) {
      this.logger.error('[CREATE-COMMESSA-DIALOG] Errore durante la creazione della commessa:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Impossibile creare la commessa. Riprova più tardi.',
        life: 5000
      });
    } finally {
      this.saving = false;
    }
  }

  /**
   * Chiude il dialog
   */
  closeDialog(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.resetForm();
  }

  /**
   * Reset del form
   */
  private resetForm(): void {
    this.codice = '';
    this.descrizione = '';
    this.clienteId = null;
    this.saving = false;
  }
}
